/**
 * Recalculate dependent fields using imported formulas.
 * Wraps recalculateFieldsLogic with template state and store.
 */
import { useCallback } from 'react';
import { useStore } from 'hooks/useStore';
import { recalculateFields as recalculateFieldsLogic } from '../bidAnalysisTemplate.logic';
import type { ContractPriceRankingItem } from './useBidAnalysisState';

export type RecalculationStateBag = {
  setWasGsmsProfitManuallySet: React.Dispatch<React.SetStateAction<boolean>>;
  templateData: any;
  activeYearTab: number | null;
  contractPriceRanking: ContractPriceRankingItem[];
  supplierName: string;
  analysisType: string;
};

export function useRecalculation(state: RecalculationStateBag) {
  const { BidAnalysisStore } = useStore();
  const {
    setWasGsmsProfitManuallySet,
    templateData,
    activeYearTab,
    contractPriceRanking,
    supplierName,
    analysisType,
  } = state;

  const recalculateFields = useCallback(
    (paramsOrUpdatedData: any, changedField?: string) => {
      let updatedData: any;
      let actualChangedField: string | undefined;

      if (
        paramsOrUpdatedData &&
        typeof paramsOrUpdatedData === 'object' &&
        !Array.isArray(paramsOrUpdatedData) &&
        'updatedData' in paramsOrUpdatedData
      ) {
        updatedData = paramsOrUpdatedData.updatedData;
        actualChangedField = paramsOrUpdatedData.changedField;
      } else {
        updatedData = paramsOrUpdatedData;
        actualChangedField = changedField;
      }

      if (actualChangedField === 'gsmsProfit') {
        setWasGsmsProfitManuallySet(true);
      }
      if (actualChangedField === 'gsmsCostFromMfg' || actualChangedField === 'gsmsNetCostFromMfg') {
        setWasGsmsProfitManuallySet(false);
      }

      const bidDetails = BidAnalysisStore.currentBidDetails;
      const currentTemplateType = bidDetails?.template_type || 'mas';
      const productType: 'direct' | 'indirect' | null = bidDetails?.product_type ?? null;

      const yearIndexForRecalc =
        currentTemplateType === 'nc' && activeYearTab !== null && activeYearTab !== 5
          ? activeYearTab
          : undefined;

      return recalculateFieldsLogic({
        updatedData,
        changedField: actualChangedField,
        productType,
        templateData,
        analysisType,
        contractPriceRanking: contractPriceRanking as Array<{ price_rank: number; price: number }>,
        templateType: currentTemplateType,
        supplierName,
        yearIndex: yearIndexForRecalc,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      setWasGsmsProfitManuallySet,
      templateData,
      activeYearTab,
      contractPriceRanking,
      supplierName,
      analysisType,
    ]
  );

  return { recalculateFields };
}
